import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { getSituationLabel } from "@utils/translator/individuals";
import { useNavigate } from "react-router-dom";
import {
  formatCoordinates,
  formatDate,
  formatDateTimeForAPI,
} from "@utils/formatters";
import type { Individual } from "@typings/entities/individuals";
import TableFilter from "./TableFilter";
import { IndividualsColumns } from "@typings/entities/individuals";
import { useGetIndividuals } from "@services/individuals/list";
import { config } from "@/config";
import type { IndividualsFilters } from "@typings/entities/individuals/table";
import { useCallback, useEffect, useState } from "react";
import { defaultFilters } from "@typings/entities/individuals/filter";
import type { GetIndividualsResponse } from "@services/individuals/type";
import { AppError } from "@utils/errors/appError";
import { toast } from "react-toastify";
import { useExportIndividuals } from "@hooks/exports/useExportIndividuals";
import { usePermissions } from "@hooks/usePermissions";

function prepareIndividuals(individuals: GetIndividualsResponse): Individual[] {
  return individuals.items.map((individual) => ({
    ...individual,
    lastCoordinates: formatCoordinates(
      individual.lastLocationLatitude,
      individual.lastLocationLongitude,
    ),
    lastEntranceDate: formatDate(individual.lastEntranceDate || null),
    lastReleaseDate: formatDate(individual.lastReleaseDate || null),
    situation: getSituationLabel(individual.situation),
    species: individual.taxonomicGroup?.species || null,
  }));
}

function TableView() {
  const { openModal, setDataModal } = usePage();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [filters, setFilters] = useState<IndividualsFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<IndividualsFilters>(defaultFilters);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);

  const { handleExport, isLoading: isExporting } = useExportIndividuals();

  const {
    data: individuals,
    isLoading,
    error,
  } = useGetIndividuals({
    ...appliedFilters,
    page: page + 1,
    limit: rowsPerPage,
  });

  const heads = IndividualsColumns;

  const handleExportIndividuals = (filters: IndividualsFilters) => async () => {
    handleExport(filters);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = useCallback(
    (field: keyof IndividualsFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    const applyFilters = { ...filters };
    applyFilters.createdAtStart = formatDateTimeForAPI(filters.createdAtStart);
    applyFilters.createdAtEnd = formatDateTimeForAPI(filters.createdAtEnd);
    applyFilters.releasedAtStart = formatDateTimeForAPI(
      filters.releasedAtStart,
    );
    applyFilters.releasedAtEnd = formatDateTimeForAPI(filters.releasedAtEnd);

    setAppliedFilters(applyFilters);
    setPage(0);
  }, [filters]);

  const processedIndividuals = individuals
    ? prepareIndividuals(individuals)
    : [];

  useEffect(() => {
    if (error) {
      const isAppError = error instanceof AppError;

      const title = isAppError
        ? error.message
        : "Erro ao carregar os indivíduos! Tente novamente!";
      toast.error(title, { autoClose: 6000 });
    }
  }, [error]);

  useEffect(() => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  }, []);

  const handleView = (individual: Individual) => {
    navigate(`/individuals/${individual.id}`);
  };

  const handleEdit = (individual: Individual) => {
    setDataModal({ id: individual.id });
    openModal("editIndividual");
  };

  const canCreateIndividual = can("create", "individuals");
  const canUpdateIndividual = can("update", "individuals");

  return (
    <div className="w-full">
      <TableFilter
        filters={filters}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
      />

      <div className="w-full mt-6 flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <TableHeader.Root>
          {canCreateIndividual && (
            <TableHeader.Button
              customVariant="secondary"
              startIcon={<AddIcon />}
              onClick={() => openModal("newIndividual")}
            >
              Novo Indivíduo
            </TableHeader.Button>
          )}
          <TableHeader.Button
            customVariant="dark"
            onClick={handleExportIndividuals(appliedFilters)}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar .csv"}
          </TableHeader.Button>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={true} />
          <Table.Body
            rows={processedIndividuals}
            columns={heads.map((head) => head.id)}
            actions={canUpdateIndividual ? ["view", "edit"] : ["view"]}
            onView={handleView}
            onEdit={canUpdateIndividual ? handleEdit : undefined}
          />
          <Table.Footer
            colSpan={heads.length}
            count={individuals?.meta.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>
    </div>
  );
}

export default TableView;
